/**
 * Port of `CarRent-back/utiles/rentalDays.js` (the server's single source of truth for
 * «how many days does this rental bill?»).
 *
 *   base  = whole calendar days between the two dates (min 1)
 *   extra = +1 day when the return time is 2 h (120 min) or more after the pickup time
 *
 * Dates are ISO `yyyy-MM-dd` strings, read as calendar days (no time zone involved), exactly what
 * the server gets from `new Date('yyyy-MM-dd')` + UTC date parts.
 */
export const EXTRA_DAY_GRACE_MINUTES = 120;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function toMinutes(t: string | null | undefined): number {
  if (!t || typeof t !== 'string') return 0;
  const [h, m] = t.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Days since the epoch for a `yyyy-MM-dd` string, NaN when invalid. */
export function dayNumber(iso: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return NaN;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / MS_PER_DAY;
}

/** Calendar nights between two ISO dates (may be 0 or negative). */
export function calendarNights(start: string, end: string): number {
  return Math.round(dayNumber(end) - dayNumber(start));
}

export function rentalDaysForPricing(start: string, end: string, pickupTime: string, returnTime: string): number {
  const s = dayNumber(start);
  const e = dayNumber(end);
  if (Number.isNaN(s) || Number.isNaN(e)) return 1;
  const base = Math.max(1, Math.round(e - s));
  const extra = toMinutes(returnTime) - toMinutes(pickupTime) >= EXTRA_DAY_GRACE_MINUTES ? 1 : 0;
  return base + extra;
}
